import React from 'react';
import I18n from 'i18n-js';
import bind from '../bind';
import Modal, { Size } from './modal';

import CsrfForm from '../csrf_form';

interface IProps {
  id?: string;
  isOpen: boolean;
  headerTitle: string;
  onSubmit?: Function;
  onRequestClose?: Function;
  headerSubtitle?: string;
  saveButtonText?: string;
  backButtonText?: string;
  onDelete?: Function | undefined;
  deleteButtonText?: string;
  onBack?: Function;
  isSaving?: boolean;
  isDisabled?: boolean;
  hideBackLink?: boolean;
  sidebarContent?: JSX.Element | React.ReactNode;
  size?: Size;
  formId?: string;

  useCsrfForm?: boolean;
  formAction?: string;

  // if the contents passed to ModalDialog already include
  // a form element, set this prop to true and ModalDialog
  // won't build its own <form>:
  containsFormElement?: boolean;
}

export default class ModalDialog extends React.Component<IProps, any> {
  @bind
  handleBack(e) {
    e.preventDefault();
    this.props.onBack();
  }

  @bind
  handleSubmit(e) {
    if (!this.props.onSubmit) return true;
    e.preventDefault();
    this.props.onSubmit();
  }

  @bind
  handleClose(e) {
    e.preventDefault();
    this.props.onRequestClose();
  }

  @bind
  handleDelete(e) {
    e.preventDefault();
    this.props.onDelete();
  }

  @bind
  handleCloseOrBack(e) {
    if (this.props.onBack) {
      this.handleBack(e);
    } else {
      this.handleClose(e);
    }
  }

  get saveButtonText() {
    if (this.props.isSaving) {
      return I18n.t('commons.modalDialog.wait');
    }
    return this.props.saveButtonText || I18n.t('commons.modalDialog.save');
  }

  renderDelete() {
    if (!this.props.onDelete) return null;

    return (
      <div>
        <button
          className="reverb-modal__actions__delete"
          onClick={this.handleDelete}
        >
          {this.props.deleteButtonText || I18n.t('commons.modalDialog.delete')}
        </button>
      </div>
    );
  }

  get buttonFormIdProp() {
    if (!this.props.formId) { return {}; }

    return { form: this.props.formId };
  }

  get saveButtonProps() {
    const semanticHtmlProps = {
      ...this.buttonFormIdProp,
      className: 'reverb-modal__actions__save',
      type: 'submit',
      disabled: (this.props.isSaving || this.props.isDisabled),
    } as React.ButtonHTMLAttributes<HTMLButtonElement>;

    if (this.props.containsFormElement) {
      return { ...semanticHtmlProps, onClick: this.handleSubmit };
    }
    return semanticHtmlProps;
  }

  renderActions() {
    return (
      <div className="reverb-modal__actions">
        <div>
          <button {...this.saveButtonProps}>
            {this.saveButtonText}
          </button>
          {!this.props.hideBackLink &&
            <button
              className="reverb-modal__actions__back"
              onClick={this.handleCloseOrBack}
            >
              {this.props.backButtonText || I18n.t('commons.modalDialog.goBack')}
            </button>
          }
        </div>
        {this.renderDelete()}
      </div>
    );
  }

  renderBackButton() {
    if (!this.props.onBack) return null;

    return (
      <button
        className="reverb-modal__back"
        onClick={this.handleBack}
      />
    );
  }

  renderModalContents() {
    return (
      <div>
        <div className="reverb-modal__dialog-body">
          {this.props.children}
        </div>
        {this.renderActions()}
        {this.renderBackButton()}
      </div>
    );
  }

  renderFormOrDiv() {
    if (this.props.containsFormElement) {
      return this.renderModalContents();
    }

    if (this.props.useCsrfForm) {
      return (
        <CsrfForm
          action={this.props.formAction || '#'}
          onSubmit={this.handleSubmit}
          method="post"
        >
          {this.renderModalContents()}
        </CsrfForm>
      );
    }

    return (
      <form
        action={this.props.formAction || '#'}
        onSubmit={this.handleSubmit}
      >
        {this.renderModalContents()}
      </form>
    );
  }

  render() {
    return (
      <Modal
        id={this.props.id}
        isOpen={this.props.isOpen}
        onRequestClose={this.props.onRequestClose}
        headerTitle={this.props.headerTitle}
        headerSubtitle={this.props.headerSubtitle}
        shouldCloseOnOverlayClick
        newClose
        sidebarContent={this.props.sidebarContent}
        size={this.props.size}
      >
        {this.renderFormOrDiv()}
      </Modal>
    );
  }
}
